import { EN_SEGMENTS, OFFICE_SLUG, toTrPath } from "@/i18n/routes";

// GA4 olay sözlüğü (tipli) ve saf yardımcılar: sayfa türü (content_group), sayfa adresinin temizlenmesi, kişisel veri
// ayıklama, tur öğesi (items[]), bağlantı tıklamasının sınıflanması. Tarayıcıya özgü kod yok (birim testi:
// tests/unit/analytics.test.ts). Olay tablosu ve parametreler: docs/analytics.md → "Olay sözlüğü".

/** GA4 e-ticaret öğesi (tur). Fiyat yalnızca EUR ve fiyatlar sitede gösteriliyorsa. */
export type AnalyticsItem = {
  item_id: string;
  item_name: string;
  /** Bölge (adres parçası: "avrupa"); iki dilde aynı */
  item_category?: string;
  /** Destinasyon (kategori adres parçası: "italya-turlari") */
  item_category2?: string;
  /** Ürün ailesi ("fly-elegant") */
  item_brand?: string;
  price?: number;
  index?: number;
  item_list_id?: string;
  item_list_name?: string;
};

export type ContentGroup =
  | "home"
  | "tour"
  | "listing"
  | "region"
  | "destination"
  | "family"
  | "dmc"
  | "agencies"
  | "about"
  | "contact"
  | "legal"
  | "not_found"
  | "other";

export type ContactMethod = "phone" | "email" | "whatsapp" | "maps";
export type ShareMethod = "whatsapp" | "facebook" | "x" | "linkedin" | "telegram" | "email" | "copy" | "native";
export type CodeSearchResult = "exact" | "departure" | "partial" | "none";

/** Olay adı → parametreleri. Yeni olay buraya eklenir (track() yalnızca bunları kabul eder). */
export type EventMap = {
  page_view: {
    page_location: string;
    page_title: string;
    page_referrer?: string;
    language: string;
    content_group: ContentGroup;
  };
  view_item_list: { item_list_id: string; item_list_name: string; currency?: "EUR"; items: AnalyticsItem[] };
  select_item: { item_list_id: string; item_list_name: string; currency?: "EUR"; items: AnalyticsItem[] };
  view_item: { currency?: "EUR"; value?: number; departure_count: number; items: AnalyticsItem[] };
  select_departure: { item_id: string; departure_date: string; availability: string; price?: number; currency?: "EUR" };
  search: {
    search_term?: string;
    search_location: "home_hero" | "listing_filters";
    region?: string;
    destination?: string;
    month?: string;
    family?: string;
    tour_code?: string;
  };
  view_search_results: {
    search_term?: string;
    result_count: number;
    region?: string;
    destination?: string;
    month?: string;
    family?: string;
    tour_code?: string;
    filter_count: number;
  };
  tour_code_search: { tour_code: string; result: CodeSearchResult };
  find_agency_click: { location: string; item_id?: string };
  generate_lead: { lead_type: "agency_application"; form_name: string };
  form_start: { form_name: string };
  contact_click: { method: ContactMethod; location: string };
  agency_login_click: { location: string };
  share: { method: ShareMethod; content_type: "tour"; item_id: string };
  page_not_found: { page_path: string };
  api_error: { page_path: string; error_digest?: string };
  tours_unavailable: { page_path: string };
};
export type EventName = keyof EventMap;

/** GA4 "key event" (dönüşüm) olarak işaretlenmesi önerilenler: GA4 arayüzünde sahip işaretler (docs/analytics.md) */
export const RECOMMENDED_KEY_EVENTS = ["generate_lead", "find_agency_click", "contact_click"] as const;

// --- Kişisel veri ayıklama ---

const EMAIL = /[^\s@]+@[^\s@]+\.[^\s@]+/g;
// 7+ rakam (aralarında boşluk, tire, nokta, parantez olabilir): telefon, T.C. kimlik, kart numarası gibi
const LONG_NUMBER = /\+?\d[\d\s().-]{5,}\d/g;

/**
 * Serbest metinden kişisel veri olabilecek parçaları siler (e-posta, uzun numara) ve kısaltır. Arama kutusuna yazılan
 * metin GA'ya ancak bundan geçerek gider; boş kalırsa undefined.
 */
export function scrubText(value: string | undefined | null, max = 100): string | undefined {
  if (!value) return undefined;
  const clean = value.replace(EMAIL, "[e-posta]").replace(LONG_NUMBER, "[numara]").replace(/\s+/g, " ").trim().slice(0, max);
  return clean || undefined;
}

/** Tur kodu: yalnızca harf, rakam, tire, boşluk; en fazla 40 */
export function cleanCode(value: string | undefined | null): string | undefined {
  const v = (value ?? "").replace(/[^A-Za-z0-9 -]/g, "").trim().toUpperCase().slice(0, 40);
  return v || undefined;
}

// --- Sayfa adresi ---

/** page_location'da kalan sorgu parametreleri: liste filtreleri, kalkış seçimi, sayfa, kampanya (utm_*) */
const KEPT_PARAMS = new Set([
  "q", "kod", "bolge", "kategori", "aile", "ay", "kalkis", "sure", "havayolu", "fiyat", "vizesiz", "sirala", "sayfa",
  "utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "utm_id",
]);
const FREE_TEXT_PARAMS = new Set(["q", "utm_term", "utm_content"]);

/**
 * GA'ya giden sayfa adresi: köken + yol + yalnızca bilinen parametreler (sıralı). Bilinmeyen parametreler (önizleme,
 * reklam tıklama kimlikleri, başka sitelerin ekledikleri) ve #parça atılır; serbest metin parametreleri ayıklanır.
 */
export function cleanPageLocation(href: string): string {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return "";
  }
  const kept: [string, string][] = [];
  for (const [key, value] of url.searchParams) {
    if (!KEPT_PARAMS.has(key)) continue;
    const v = FREE_TEXT_PARAMS.has(key) ? scrubText(value, 80) : value.slice(0, 80);
    if (v) kept.push([key, v]);
  }
  kept.sort(([a], [b]) => a.localeCompare(b));
  const qs = new URLSearchParams(kept).toString();
  return `${url.origin}${url.pathname}${qs ? `?${qs}` : ""}`;
}

// --- Sayfa türü (content_group) ---

const REGION_PAGES = new Set(["avrupa-turlari", "asya-turlari", "afrika-turlari", "amerika-turlari", "kruvaziyer-turlari"]);
const FAMILY_PAGES = new Set(["urunlerimiz", "fly-cruise", "fly-exclusive", "fly-elegant", "fly-classic"]);
// FlyDMC: /flydmc ve ofis sayfaları ("/fly-<ad>"; ürün aileleri FAMILY_PAGES ile önce ayrılır)
const isDmcPage = (first: string) => first === "flydmc" || OFFICE_SLUG.test(first);
const LEGAL_PAGES = new Set(["kvkk-aydinlatma", "gizlilik-politikasi", "cerez-politikasi"]);
const SIMPLE: Record<string, ContentGroup> = {
  acentelerimiz: "agencies",
  "acente-talep-formu": "agencies",
  hakkimizda: "about",
  iletisim: "contact",
  destinasyonlar: "destination",
};

/**
 * Tarayıcı yolundan sayfa türü (TR ve EN adresleri aynı türe düşer). Kökteki tek parçalı bilinmeyen adres destinasyon da
 * olabilir yönetilen sayfa da: sayfa `data-ga-group` işaretiyle kendisi söyler (`marker` önceliklidir; ör. destinasyon
 * görünümü, 404).
 */
export function contentGroupFor(pathname: string, marker?: string | null): ContentGroup {
  if (marker && isContentGroup(marker)) return marker;
  const tr = toTrPath(pathname);
  if (tr === null) return "other";
  const [first, ...rest] = tr.split("/").filter(Boolean);
  if (!first) return "home";
  if (first === "turlar") return rest.length ? "tour" : "listing";
  if (REGION_PAGES.has(first)) return "region";
  if (FAMILY_PAGES.has(first)) return "family";
  if (isDmcPage(first)) return "dmc";
  if (LEGAL_PAGES.has(first)) return "legal";
  if (first in SIMPLE) return SIMPLE[first];
  return "other";
}

const GROUPS: ReadonlySet<string> = new Set<ContentGroup>([
  "home", "tour", "listing", "region", "destination", "family", "dmc", "agencies", "about", "contact", "legal", "not_found", "other",
]);
export const isContentGroup = (v: string): v is ContentGroup => GROUPS.has(v);

// EN_SEGMENTS'e yeni sabit sayfa eklenince burada bir türe bağlanmalı (birim testi tüm parçaları dener)
export const KNOWN_TR_SEGMENTS = Object.keys(EN_SEGMENTS);

// --- Tur öğesi ---

export type TourItemInput = {
  code: string;
  name: string;
  region?: string | null;
  category?: string | null;
  family?: string | null;
  price?: { amount: number; currency: string } | null;
};

/** Tur → GA4 öğesi. Fiyat yalnızca fiyatlar gösteriliyorsa ve EUR ise (kur olmadan para birimleri karışmasın). */
export function tourItem(t: TourItemInput, opts: { showPrices: boolean; index?: number; list?: { id: string; name: string } }): AnalyticsItem {
  const item: AnalyticsItem = { item_id: t.code, item_name: t.name.slice(0, 100) };
  if (t.region) item.item_category = t.region;
  if (t.category) item.item_category2 = t.category;
  if (t.family) item.item_brand = t.family;
  if (opts.showPrices && t.price && t.price.currency === "EUR" && Number.isFinite(t.price.amount)) item.price = Math.round(t.price.amount * 100) / 100;
  if (opts.index !== undefined) item.index = opts.index;
  if (opts.list) {
    item.item_list_id = opts.list.id;
    item.item_list_name = opts.list.name;
  }
  return item;
}

/** Katalog özetinden öğe (ad: Türkçe aslı — iki dilde aynı raporlansın; fiyat: "başlayan" fiyat) */
export function analyticsTourItem(
  tour: { code: string; name: string; sourceName?: string; region: string | null; category: { slug: string } | null; family: string | null },
  fromPrice: { amount: number; currency: string } | null,
  showPrices: boolean,
): AnalyticsItem {
  return tourItem(
    { code: tour.code, name: tour.sourceName ?? tour.name, region: tour.region, category: tour.category?.slug, family: tour.family, price: fromPrice },
    { showPrices },
  );
}

/** Öğelerde fiyat varsa olayın para birimi (GA4 değer/fiyat için para birimi ister) */
export const itemsCurrency = (items: AnalyticsItem[]): { currency?: "EUR" } => (items.some((i) => i.price !== undefined) ? { currency: "EUR" } : {});

// --- Bağlantı tıklamaları ---

export type LinkClick =
  | { name: "contact_click"; params: EventMap["contact_click"] }
  | { name: "agency_login_click"; params: EventMap["agency_login_click"] }
  | null;

const WHATSAPP = /^https?:\/\/(?:wa\.me|api\.whatsapp\.com|web\.whatsapp\.com)\//i;
const MAPS = /^https?:\/\/(?:(?:www\.)?google\.[a-z.]+\/maps|maps\.google\.[a-z.]+|maps\.app\.goo\.gl|goo\.gl\/maps)/i;

/** Aynı köken (alan adı birebir) ve panel yolunun altı mı ("https://flex.flyexpress.com.evil.example" eşleşmez) */
function isUnder(href: string, base: string): boolean {
  try {
    const a = new URL(href);
    const b = new URL(base);
    if (a.origin !== b.origin) return false;
    const prefix = b.pathname.replace(/\/+$/, "");
    return a.pathname === prefix || a.pathname.startsWith(`${prefix}/`) || prefix === "";
  } catch {
    return false;
  }
}

/**
 * Tıklanan bağlantının olayı: tel: / mailto: / WhatsApp / harita → contact_click, acente paneli → agency_login_click.
 * `area` bağlantının bulunduğu yer (üst menü, altbilgi, rezervasyon kutusu …: en yakın data-ga-area). Paylaşım
 * düğmeleri bu sınıflamaya girmez (share olayı ayrı).
 */
export function classifyLink(href: string, ctx: { area: string; agencyPanelUrl?: string }): LinkClick {
  const h = href.trim();
  if (/^tel:/i.test(h)) return { name: "contact_click", params: { method: "phone", location: ctx.area } };
  if (/^mailto:/i.test(h)) return { name: "contact_click", params: { method: "email", location: ctx.area } };
  if (WHATSAPP.test(h)) return { name: "contact_click", params: { method: "whatsapp", location: ctx.area } };
  if (MAPS.test(h)) return { name: "contact_click", params: { method: "maps", location: ctx.area } };
  if (ctx.agencyPanelUrl && /^https?:\/\//i.test(h) && isUnder(h, ctx.agencyPanelUrl)) {
    return { name: "agency_login_click", params: { location: ctx.area } };
  }
  return null;
}

/** Olay parametrelerinden undefined/boş değerleri atar (GA'ya "undefined" yazısı gitmesin) */
export function compactParams<T extends Record<string, unknown>>(params: T): T {
  return Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "")) as T;
}
